import { Suspense, useState, useEffect, lazy } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography, styled, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import styles from "../styles/components/landing.module.scss";
import classNames from "classnames";
import { FaFacebookF, FaInstagram, FaTiktok, FaGlobe } from "react-icons/fa";
import { LanguageSwitcherLanding } from "../components/LanguageSwitcher";
import { loadTenantAsset } from "../redux/tenantSlice";
import { setUTMSource } from "../redux/utmSlice";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { min } from "date-fns";

// Lazy loaded components
const RecettesModal = lazy(() => import("../components/RecettesModal"));
const AlertModal = lazy(() => import("../components/AlertModal"));
const TermsOfUse = lazy(() => import("../components/TermsOfUse").then((module) => ({ default: module.TermsOfUse })));
const TermsOfUsePopup = lazy(() => import("../components/TermsOfUse").then((module) => ({ default: module.TermsOfUsePopup })));

// Styled components
const MainContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "white",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  overflow: "hidden",
  justifyContent: "space-between",
  [theme.breakpoints.between("md", "lg")]: {
    gap: "1%",
    paddingTop: "60px",
  },
  [theme.breakpoints.up("lg")]: {
    gap: "2%",
  },
}));

const FormImageContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "95%",
  padding: "0px 4%",
  flexDirection: "column",
  boxSizing: "border-box",
  justifyContent: "center",
  maxHeight: "80vh",
  marginBottom: "10px",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    width: "85%",
    maxWidth: "1300px",
    height: "auto",
    minHeight: "50vh",
    background: "linear-gradient(0deg, rgb(255, 255, 255) 0%, rgb(244, 247, 251) 100%)",
    borderRadius: "30px",
    padding: "1.5% 2.5%",
    border: "3px solid rgb(255, 255, 255)",
    boxShadow: "rgba(133, 189, 215, 0.8784313725) 0px 20px 25px -15px",
    margin: "15px 0",
    alignItems: "center",
    gap: "25px",
  },
  [theme.breakpoints.between("md", "lg")]: {
    width: "90%",
    maxWidth: "1200px",
    minHeight: "45vh",
    padding: "1% 2%",
    gap: "20px",
    margin: "10px 0",
  },
  [theme.breakpoints.between("sm", "md")]: {
    width: "95%",
    height: "70%",
    background: "linear-gradient(0deg, rgb(255, 255, 255) 0%, rgb(244, 247, 251) 100%)",
    borderRadius: "40px",
    padding: "5%",
    border: "5px solid rgb(255, 255, 255)",
    boxShadow: "rgba(133, 189, 215, 0.8784313725) 0px 30px 30px -20px",
  },
}));

const NavigationButton = styled(Button)(({ theme, buttontype }) => ({
  fontWeight: "600 !important",
  textTransform: "none !important",
  margin: "6px 0",
  height: "42px",
  color: "var(--complaintTextColor)",
  border: "none",
  animation: "slideGradient 3s linear infinite",
  background:
    buttontype === "consumer"
      ? "var(--merchant-button)"
      : buttontype === "complaint"
      ? "var(--complaint-button)"
      : buttontype === "other"
      ? "var(--other-request-button)"
      : buttontype === "recipe"
      ? "var(--astuce-button)"
      : "var(--consumer-button)",
  backgroundSize: "150% 100%",
  display: "flex",
  textAlign: "center",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap !important",
  width: "100%",
  borderRadius: "25px",
  fontSize: "16px",
  padding: "2.5%",
  cursor: "pointer",
  [theme.breakpoints.between("md", "lg")]: {
    height: "40px",
    fontSize: "15px",
    margin: "5px 0",
    padding: "2%",
  },
  [theme.breakpoints.up("lg")]: {
    height: "50px",
    fontSize: "18px",
    borderRadius: "27px",
    padding: "3%",
  },
  "@keyframes slideGradient": {
    "0%, 100%": {
      backgroundPosition: "0% 50%",
    },
    "50%": {
      backgroundPosition: "100% 50%",
    },
  },
}));

const LineWithText = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  textAlign: "center",
  margin: "8px 0",
  color: "#7a736d",
  width: "100%",
  "& .line": {
    flex: 1,
    height: "1px",
    opacity: "50%",
    backgroundColor: "#7a736d",
    minWidth: "20px",
  },
  "& span": {
    margin: "0 10px",
    whiteSpace: "nowrap",
    color: "#7a736d",
    fontSize: "14px",
  },
  [theme.breakpoints.between("md", "lg")]: {
    margin: "6px 0",
    "& span": {
      fontSize: "13px",
      margin: "0 8px",
    },
  },
}));

const Landing = ({ isPWAInstallable, onInstallClick, isIos, currentLang, setCurrentLang, flag, setFlag }) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [isTermsOfUseModalVisible, setIsTermsOfUseModalVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [formType, setFormType] = useState(null);
  const [isOpenRecipe, setIsOpenRecipe] = useState(false);
  const tenantState = useSelector((state) => state.tenant);
  const bg = tenantState?.tenant.assets?.bg;
  const tenantName = useSelector((state) => state.tenant?.tenant.name) || "base";
  const enabledModules = useSelector((state) => state.tenant?.tenant.enabledModules) || {};
  const socialMedia = useSelector((state) => state.tenant?.tenant.socialMedia) || {};
  const [formsBtnVisible, setFormsBtnVisible] = useState(false);
  const [isGCUPopupOpened, setGCUPopupOpened] = useState(false);
  const navigate = useNavigate();

  const onAccept = () => {
    localStorage.setItem("conditionsAccepted", "true");
    setIsTermsOfUseModalVisible(false);

    switch (formType) {
      case "otherRequest":
        navigate("/contactez-nous");
        break;
      case "form":
        navigate("/reclamation_q");
        break;
      case "distributor":
        navigate("/reclamation_d");
        break;
      default:
        return;
    }
  };

  const onRefuse = () => {
    setIsTermsOfUseModalVisible(false);
  };

  const goToForm = () => {
    const conditionsAccepted = localStorage.getItem("conditionsAccepted");
    setFormType("form");
    if (!conditionsAccepted && enabledModules.termsOfUse) {
      setIsTermsOfUseModalVisible(true);
    } else {
      navigate("/reclamation_q");
    }
  };

  const goToDistributorForm = () => {
    const conditionsAccepted = localStorage.getItem("conditionsAccepted");
    setFormType("distributor");
    if (!conditionsAccepted && enabledModules.termsOfUse) {
      setIsTermsOfUseModalVisible(true);
    } else {
      navigate("/reclamation_d");
    }
  };

  const goToOtherRequestForm = () => {
    const conditionsAccepted = localStorage.getItem("conditionsAccepted");
    setFormType("otherRequest");
    if (!conditionsAccepted && enabledModules.termsOfUse) {
      setIsTermsOfUseModalVisible(true);
    } else {
      navigate("/contactez-nous");
    }
  };

  const showAlerts = () => {
    setOpen(true);
  };

  const openRecipe = () => {
    setIsOpenRecipe(true);
  };

  const toggleFormsButtons = () => {
    setFormsBtnVisible(!formsBtnVisible);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlUTM = params.get("utm");
    console.log("utmSource landing", urlUTM);

    if (urlUTM) {
      dispatch(setUTMSource(urlUTM));
      sessionStorage.setItem("utm_source", urlUTM);
    } else {
      const storedUTM = sessionStorage.getItem("utm_source");
      if (storedUTM) dispatch(setUTMSource(storedUTM));
    }
  }, []);

  return (
    <MainContainer>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          padding: "10px",
          top: 0,
          position: "fixed",
          zIndex: 15,
          [theme.breakpoints.between("md", "lg")]: {
            padding: "8px",
          },
          [theme.breakpoints.up("lg")]: {
            padding: "1%",
          },
        }}
      >
        <LanguageSwitcherLanding
          currentLang={currentLang}
          setCurrentLang={setCurrentLang}
          flag={flag}
          setFlag={setFlag}
        />
        <Button
          onClick={showAlerts}
          sx={{
            backgroundColor: "white",
            boxShadow:
              "0 2px 4px rgb(148 142 142 / 60%), inset 0 2px 4px rgba(255, 255, 255, 0.3)",
            borderRadius: "50%",
            outline: "none",
            justifyContent: "center",
            padding: "8px 9px",
            color: "#ffc300",
            minWidth: "auto",
            "&:focus": { outline: "none" },
            "&:active": { outline: "none", border: "none" },
          }}
        >
          <FontAwesomeIcon icon={faBell} style={{ fontSize: "18px" }} />
        </Button>
      </Box>

      <Header />

      <FormImageContainer>
        <Box
          component="form"
          sx={{
            width: "100%",
            display: "flex",
            marginTop: "30px",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: { xs: "25px", md: 0 },
            [theme.breakpoints.between("md", "lg")]: {
              width: "52%",
              padding: "0 15px",
              justifyContent: "center",
              marginTop: "40px",
            },
            [theme.breakpoints.up("lg")]: {
              width: "50%",
              padding: "0 20px",
              justifyContent: "center",
            },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              margin: "auto",
              marginBottom: "6px",
              fontSize: {
                xs: "1rem",
                sm: "1.1rem",
                md: "1.1rem",
                lg: "1.2rem",
              },
              color: "#7a736d!important",
              textAlign: "center",
              fontWeight: 400,
              width: "100%",
              overflowWrap: "break-word",
              wordWrap: "break-word",
              padding: "0 10px",
              boxSizing: "border-box",
            }}
          >
            {t("Bienvenue")}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              width: "100%",
              overflowWrap: "break-word",
              wordWrap: "break-word",
              padding: "0 10px",
              boxSizing: "border-box",
              fontSize: {
                xs: "0.9rem",
                sm: "0.95rem",
                md: "0.9rem",
                lg: "1rem",
              },
              color: "#7a736d !important",
              marginBottom: "8px",
            }}
          >
            {t("AreYou")}
          </Typography>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              margin: "10px 0",
              justifyContent: "center",
              flexDirection: "column",
              gap: "8px",
              [theme.breakpoints.between("md", "lg")]: {
                width: "100%",
                maxWidth: "450px",
                gap: "6px",
                margin: "8px 0",
              },
              [theme.breakpoints.up("lg")]: {
                width: "100%",
                maxWidth: "500px",
                gap: "10px",
                margin: "15px 0",
              },
            }}
          >
            {enabledModules.merchantModule && enabledModules.consumerModule && (
              <>
                <NavigationButton
                  buttontype="complaint"
                  onClick={toggleFormsButtons}
                >
                  {t("complaint")}
                </NavigationButton>

                {formsBtnVisible && (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      gap: "8px",
                      flexDirection: "row",
                      [theme.breakpoints.between("md", "lg")]: {
                        gap: "6px",
                      },
                    }}
                  >
                    {enabledModules.merchantModule && (
                      <NavigationButton
                        buttontype="default"
                        onClick={goToDistributorForm}
                      >
                        {t("distributor")}
                      </NavigationButton>
                    )}

                    {enabledModules.consumerModule && (
                      <NavigationButton
                        buttontype="consumer"
                        onClick={goToForm}
                      >
                        {t("consumer")}
                      </NavigationButton>
                    )}
                  </Box>
                )}
              </>
            )}

            {enabledModules.merchantModule &&
              !enabledModules.consumerModule && (
                <NavigationButton
                  buttontype="default"
                  onClick={goToDistributorForm}
                >
                  {t("complaint")}
                </NavigationButton>
              )}

            {!enabledModules.merchantModule &&
              enabledModules.consumerModule && (
                <NavigationButton buttontype="consumer" onClick={goToForm}>
                  {t("complaint")}
                </NavigationButton>
              )}

            {enabledModules.OtherRequest && (
              <NavigationButton
                buttontype="other"
                onClick={goToOtherRequestForm}
              >
                {t("other_request")}
              </NavigationButton>
            )}
          </Box>

          {(enabledModules.tipsModule ||
            enabledModules.recipesModule ||
            enabledModules.tipsRecipesModule) && (
            <LineWithText>
              <Box className="line" />
              <span>{t("forYou")}</span>
              <Box className="line" />
            </LineWithText>
          )}

          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              marginBottom: "15px",
              justifyContent: "center",
              flexDirection: "column",
              gap: "8px",
              [theme.breakpoints.between("md", "lg")]: {
                width: "100%",
                maxWidth: "450px",
                gap: "6px",
                marginBottom: "10px",
              },
              [theme.breakpoints.up("lg")]: {
                width: "100%",
                maxWidth: "500px",
                gap: "10px",
                marginBottom: "20px",
              },
            }}
          >
            {enabledModules.tipsRecipesModule ? (
              <NavigationButton buttontype="recipe" onClick={openRecipe}>
                {t("buttonForYou")}
              </NavigationButton>
            ) : enabledModules.recipesModule ? (
              <NavigationButton buttontype="recipe" onClick={openRecipe}>
                {t("buttonForYouRecipes")}
              </NavigationButton>
            ) : enabledModules.tipsModule ? (
              <NavigationButton buttontype="recipe" onClick={openRecipe}>
                {t("buttonForYouTips")}
              </NavigationButton>
            ) : null}
          </Box>
        </Box>
        {bg && (
          <Box
            component="img"
            // sx={{
            //   width: { xs: "50%", sm: "58%", md: "44%", lg: "45%" },
            //   // iPhone SE screen
            //   "@media (max-width: 380px)": {
            //     width: "108px !important",
            //     minWidth: "108px !important",
            //     maxWidth: "108px !important",
            //   },
            //   maxWidth: { sm: "none", md: "420px", lg: "500px" },
            //   margin: "auto",
            //   aspectRatio: "auto",
            //   maxHeight: "45vh",
            //   objectFit: "contain",
            //   [theme.breakpoints.between("sm", "md")]: {
            //     height: "45%",
            //     width: "auto",
            //     paddingBottom: "50px",
            //     maxWidth: "none",
            //   },
            //   [theme.breakpoints.between("md", "lg")]: {
            //     maxHeight: "40vh",
            //     maxWidth: "380px",
            //   },
            // }}
            sx={{
              width: { xs: "20vh", sm: "40vh", md: "50vh", lg: "60vh" }, // width follows viewport height
              height: "auto", // keep aspect ratio
              margin: "auto",
              objectFit: "contain",
            }}
            src={loadTenantAsset(tenantName, "bg")}
            alt="Default Background"
            srcSet={`
              ${loadTenantAsset(tenantName, "bg")} 400w,
              ${loadTenantAsset(tenantName, "bg")} 800w,
              ${loadTenantAsset(tenantName, "bg")} 1200w
            `}
            sizes="(max-width: 600px) 100vw, (max-width: 1024px) 60vw, 800px"
            loading="eager"
            decoding="async"
          />
        )}
      </FormImageContainer>

      <div className={styles.socialIconsWrapper}>
        {socialMedia.facebook && (
          <div className={classNames(styles.iconContainer, styles.facebook)}>
            <a
              href={socialMedia.facebook}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaFacebookF className={styles.socialMediaIcon} />
            </a>
          </div>
        )}
        {socialMedia.instagram && (
          <div className={classNames(styles.iconContainer, styles.instagram)}>
            <a
              href={socialMedia.instagram}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaInstagram className={styles.socialMediaIcon} />
            </a>
          </div>
        )}
        {socialMedia.tiktok && (
          <div className={classNames(styles.iconContainer, styles.tiktok)}>
            <a
              href={socialMedia.tiktok}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaTiktok className={styles.socialMediaIcon} />
            </a>
          </div>
        )}

        {socialMedia.webSite && (
          <div className={classNames(styles.iconContainer, styles.website)}>
            <a
              href={socialMedia.webSite}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaGlobe className={styles.socialMediaWebIcon} />
            </a>
          </div>
        )}

        {enabledModules.termsOfUse && (
          <div>
            <button
              className={styles.gcuButton}
              onClick={() => {
                setGCUPopupOpened(true);
              }}
            >
              CGU
            </button>
          </div>
        )}
      </div>

      <Footer />

      <Suspense fallback={null}>
        {isOpenRecipe && (
          <RecettesModal isOpen={isOpenRecipe} setOpen={setIsOpenRecipe} />
        )}
        <AlertModal open={open} setOpen={setOpen} />
        <TermsOfUse
          isVisible={isTermsOfUseModalVisible}
          onAccept={onAccept}
          onRefuse={onRefuse}
        />
        <TermsOfUsePopup
          isOpen={isGCUPopupOpened}
          setOpen={setGCUPopupOpened}
        />
      </Suspense>
    </MainContainer>
  );
};

export default Landing;
